import { BlockDiagramViewSettings } from '../../../tabview/blockDiagram/types';
import {
  GraphComponent,
  HierarchicalLayoutConfig,
  HierarchicalLayoutData,
  HierarchicalLayoutEdgeDescriptor,
  HierarchicalLayoutRoutingStyle,
  INode,
  Insets,
  LayoutDescriptor,
  LayoutOrientation,
  PortAdjustmentPolicy,
  TimeSpan,
} from '@ardoq/yfiles';
import { GraphItem, Rect, inside, GRAPH_INSETS } from '@ardoq/graph';
import { getCriticalPaths } from '../../../tabview/blockDiagram/view/criticalPaths';
import { maximumMarkerWidth } from 'svg/definitions/consts';
import { isContextNode } from '../../utils';
import { SimpleGraphViewProps } from '../../types';
import { maximumGraphLayoutDuration } from 'tabview/relationshipDiagrams/maximumGraphLayoutDuration';
import { CONTEXT_HIGHLIGHT_PADDING } from 'yfilesExtensions/styles/consts';

const standardHierarchicLayoutOptions: HierarchicalLayoutConfig = {
  nodeLabelPlacement: 'consider',
  edgeLabelPlacement: 'integrated',
  defaultEdgeDescriptor: new HierarchicalLayoutEdgeDescriptor({
    backLoopRouting: false,
    routingStyleDescriptor: {
      defaultRoutingStyle: HierarchicalLayoutRoutingStyle.ORTHOGONAL,
    },
  }),
  minimumLayerDistance: 35,
  groupLayeringPolicy: 'recursive',
  fromScratchLayeringStrategy: 'hierarchical-optimal',
};

const layoutOrientationString = (layoutOrientation: LayoutOrientation) => {
  switch (layoutOrientation) {
    case LayoutOrientation.TOP_TO_BOTTOM:
      return 'top-to-bottom';
    case LayoutOrientation.BOTTOM_TO_TOP:
      return 'bottom-to-top';
    case LayoutOrientation.LEFT_TO_RIGHT:
      return 'left-to-right';
    case LayoutOrientation.RIGHT_TO_LEFT:
      return 'right-to-left';
  }
};
const getLayoutInfo = (
  nodeCount: number,
  edgeCount: number,
  layoutOrientation: LayoutOrientation
) => {
  const layoutConfig: HierarchicalLayoutConfig = {
    ...standardHierarchicLayoutOptions,
    stopDuration: TimeSpan.fromMilliseconds(
      maximumGraphLayoutDuration(nodeCount, edgeCount)
    ),
    layoutOrientation: layoutOrientationString(layoutOrientation),
    defaultEdgeDescriptor: new HierarchicalLayoutEdgeDescriptor({
      routingStyleDescriptor: {
        defaultRoutingStyle: HierarchicalLayoutRoutingStyle.ORTHOGONAL,
      },
      minimumFirstSegmentLength: maximumMarkerWidth,
      minimumLastSegmentLength: maximumMarkerWidth,
      minimumLength: 2 * maximumMarkerWidth,
    }),
  };
  const layoutDescriptor: LayoutDescriptor = {
    name: 'HierarchicalLayout',
    properties: layoutConfig,
  };

  return layoutDescriptor;
};

const getInsets = (
  shouldFitToContentOnLayout: boolean,
  wasFullGraphDisplayed: boolean,
  insets: number
) => {
  if (shouldFitToContentOnLayout || wasFullGraphDisplayed) {
    return new Insets(insets);
  }

  return Insets.EMPTY;
};

const configureLayout = (
  graphComponent: GraphComponent,
  { graphNodes, graphEdges }: SimpleGraphViewProps['viewModel'],
  { layoutOrientation }: BlockDiagramViewSettings,
  shouldFitToContentOnLayout = true,
  insets: number = GRAPH_INSETS
) => {
  const nodeCount = graphNodes.size;
  const edgeCount = graphEdges.size;
  const layoutDescriptor = getLayoutInfo(
    nodeCount,
    edgeCount,
    layoutOrientation
  );

  if (shouldFitToContentOnLayout) {
    layoutDescriptor.properties!.fromSketchMode = false;
  }

  const { graph, contentBounds, viewport } = graphComponent;

  const layoutData = new HierarchicalLayoutData({ edgeDirectedness: 1 });

  const wasFullGraphDisplayed = inside(
    Rect.fromRectLike(contentBounds),
    Rect.fromRectLike(viewport)
  );

  const newIds = new Set([...graphNodes.keys(), ...graphEdges.keys()]);
  layoutData.incrementalNodes.predicate = ({ tag }: INode) =>
    tag instanceof GraphItem && !newIds.has(tag.id);
  const criticalPaths = getCriticalPaths({
    graph,
    contextNode: graph.nodes.find(isContextNode),
  });
  if (criticalPaths.size) {
    layoutData.criticalEdgePriorities = edge => criticalPaths.get(edge) ?? null;
  }
  layoutData.nodeMargins.mapperFunction = node =>
    isContextNode(node) ? new Insets(CONTEXT_HIGHLIGHT_PADDING) : null;

  return {
    layoutDescriptor,
    layoutData,
    duration: TimeSpan.ZERO,
    animateViewport: false,
    updateContentRect: true,
    targetBoundsInsets: getInsets(
      shouldFitToContentOnLayout,
      wasFullGraphDisplayed,
      insets
    ),
    portAdjustmentPolicy: PortAdjustmentPolicy.ALWAYS, // shorten or elongate edges according to the outline as defined by ArdoqNodeStyle/IsometricNodeStyle. this prevents the edges from appearing "on top of" the faces of the isometric blocks.
  };
};
export { configureLayout };
